// src/stores/authStore.js
import { defineStore } from 'pinia';
import AuthService from '@/services/authService';
import { useToastStore } from './toastStore';

export const useAuthStore = defineStore('auth', {
    state: () => ({
        token: localStorage.getItem('auth_token') || null,
        user: JSON.parse(localStorage.getItem('user')) || null,
        company: JSON.parse(localStorage.getItem('company')) || null,
        loading: false,
        error: null
    }),

    getters: {
        isAuthenticated: (state) => !!state.token,
        isAdmin: (state) => state.user && state.user.role === 'admin'
    },

    actions: {
        // Connexion utilisateur
        async login(credentials) {
            this.loading = true;
            this.error = null;

            try {
                console.clear()
                console.log('credentials:', credentials)
                const response = await AuthService.login(credentials);
                const { access_token, user, company } = response.data;

                // Sauvegarder les informations d'authentification
                localStorage.setItem('auth_token', access_token);
                localStorage.setItem('user', JSON.stringify(user));
                localStorage.setItem('company', JSON.stringify(company));

                // Mettre à jour l'état
                this.token = access_token;
                this.user = user;
                this.company = company;
                this.loading = false;
                this.error = null;

                // Ajouter une notification de succès
                const toastStore = useToastStore();
                toastStore.addToast({
                    type: 'success',
                    message: 'Connexion réussie. Bienvenue!'
                });

                return response.data;
            } catch (error) {
                this.loading = false;
                this.error = error.response?.data?.message || 'Erreur de connexion';
                throw error;
            }
        },

        // Déconnexion utilisateur
        async logout() {
            try {
                if (this.token) {
                    await AuthService.logout();
                }
            } catch (error) {
                console.error('Erreur lors de la déconnexion:', error);
            } finally {
                // Supprimer les informations d'authentification
                localStorage.removeItem('auth_token');
                localStorage.removeItem('user');
                localStorage.removeItem('company');

                // Réinitialiser l'état
                this.token = null;
                this.user = null;
                this.company = null;
                this.loading = false;
                this.error = null;

                // Ajouter une notification
                const toastStore = useToastStore();
                toastStore.addToast({
                    type: 'info',
                    message: 'Vous avez été déconnecté'
                });
            }
        },

        // Inscription utilisateur
        async register(userData) {
            this.loading = true;
            this.error = null;

            try {
                const response = await AuthService.register(userData);

                this.loading = false;

                // Ajouter une notification
                const toastStore = useToastStore();
                toastStore.addToast({
                    type: 'success',
                    message: 'Inscription réussie! Veuillez vérifier votre email.'
                });

                return response.data;
            } catch (error) {
                this.loading = false;
                this.error = error.response?.data?.message || "Erreur lors de l'inscription";
                throw error;
            }
        },

        // Récupérer les informations de l'utilisateur connecté
        async getUser() {
            this.loading = true;
            this.error = null;

            try {
                const response = await AuthService.getUser();
                const { user, company } = response.data;

                localStorage.setItem('user', JSON.stringify(user));
                localStorage.setItem('company', JSON.stringify(company));

                this.user = user;
                this.company = company;
                this.loading = false;

                return response.data;
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    // Token invalide, déconnecter l'utilisateur
                    this.token = null;
                    this.user = null;
                    this.company = null;
                    this.loading = false;
                    this.error = null;
                } else {
                    this.loading = false;
                    this.error = error.response?.data?.message || "Erreur lors de la récupération des informations";
                }
                throw error;
            }
        },

        // Mettre à jour le profil utilisateur
        async updateProfile(profileData) {
            try {
                const response = await AuthService.updateProfile(profileData);

                this.user = { ...this.user, ...response.data.user };
                localStorage.setItem('user', JSON.stringify(this.user));

                const toastStore = useToastStore();
                toastStore.addToast({
                    type: 'success',
                    message: 'Profil mis à jour avec succès'
                });

                return response.data;
            } catch (error) {
                const toastStore = useToastStore();
                toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || 'Erreur lors de la mise à jour du profil'
                });
                throw error;
            }
        },

        // Renvoyer l'email de vérification
        async resendVerification(email) {
            try {
                await AuthService.resendVerification(email);

                const toastStore = useToastStore();
                toastStore.addToast({
                    type: 'success',
                    message: "Un email de vérification a été envoyé à votre adresse"
                });

                return true;
            } catch (error) {
                const toastStore = useToastStore();
                toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || "Erreur lors de l'envoi de l'email de vérification"
                });
                throw error;
            }
        },

        // Réinitialiser le mot de passe - demande
        async forgotPassword(email) {
            try {
                await AuthService.forgotPassword(email);

                const toastStore = useToastStore();
                toastStore.addToast({
                    type: 'success',
                    message: "Un lien de réinitialisation a été envoyé à votre adresse email"
                });

                return true;
            } catch (error) {
                const toastStore = useToastStore();
                toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || "Erreur lors de la demande de réinitialisation"
                });
                throw error;
            }
        },

        // Réinitialiser le mot de passe - confirmation
        async resetPassword(resetData) {
            try {
                await AuthService.resetPassword(resetData);

                const toastStore = useToastStore();
                toastStore.addToast({
                    type: 'success',
                    message: "Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter."
                });

                return true;
            } catch (error) {
                const toastStore = useToastStore();
                toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || "Erreur lors de la réinitialisation du mot de passe"
                });
                throw error;
            }
        },

        // Changer le mot de passe utilisateur
        async changePassword(passwordData) {
            try {
                await AuthService.changePassword(passwordData);

                const toastStore = useToastStore();
                toastStore.addToast({
                    type: 'success',
                    message: "Votre mot de passe a été modifié avec succès"
                });

                return true;
            } catch (error) {
                const toastStore = useToastStore();
                toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || "Erreur lors du changement de mot de passe"
                });
                throw error;
            }
        },

        // Mettre à jour l'entreprise de l'utilisateur
        updateCompany(company) {
            this.company = { ...this.company, ...company };
            localStorage.setItem('company', JSON.stringify(this.company));
        },

        // Effacer les erreurs
        clearError() {
            this.error = null;
        }
    }
});